import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useBrowserInfo, useCanInstallPWA } from "@/hooks/useCanInstallPWA";
import React, { useCallback, useEffect, useRef, useState } from "react";

// Type for BeforeInstallPromptEvent (not in TS lib yet)
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

interface InstallWrapperProps {
  children: React.ReactNode;
}

export const InstallWrapper: React.FC<InstallWrapperProps> = ({ children }) => {
  const [showModal, setShowModal] = useState(false);
  const browserInfo = useBrowserInfo();
  const { canInstall } = useCanInstallPWA();

  const installPromptEvent = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      installPromptEvent.current = e as BeforeInstallPromptEvent;
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [browserInfo.device.type]);

  const handleShowModal = useCallback(() => {
    setShowModal(true);
  }, []);

  const handleInstall = useCallback(async () => {
    if (installPromptEvent.current) {
      installPromptEvent.current.prompt();
      await installPromptEvent.current.userChoice;
      installPromptEvent.current = null;
    } else {
      handleShowModal();
    }
  }, [handleShowModal, installPromptEvent]);

  if (!canInstall) return null;

  return (
    <>
      {React.cloneElement(children as React.ReactElement, {
        //@ts-expect-error - TODO: fix this
        onClick: handleInstall,
      })}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Comment installer cette application</DialogTitle>
            <DialogDescription>
              {browserInfo.os.name === "iOS" ?
                <>
                  Sur iOS, appuyez sur le bouton{" "}
                  <img
                    src="/apple-share.svg"
                    alt="Partager"
                    className="inline-block h-7 w-7"
                  />{" "}
                  puis sélectionnez{" "}
                  <span className="font-semibold">
                    Ajouter à l'écran d'accueil
                  </span>
                  .
                </>
              : (
                browserInfo.browser.name === "Chrome" &&
                browserInfo.os.name === "Android"
              ) ?
                <>
                  Sur Android Chrome, appuyez sur le{" "}
                  <span className="font-semibold">menu</span> (⋮) et
                  sélectionnez{" "}
                  <span className="font-semibold">Installer l'application</span>
                  .
                </>
              : (
                browserInfo.browser.name === "Firefox" &&
                browserInfo.os.name === "Android"
              ) ?
                <>
                  Sur Android Firefox, appuyez sur le{" "}
                  <span className="font-semibold">menu</span> (⋮) et
                  sélectionnez{" "}
                  <span className="font-semibold">Installer l'application</span>
                  .
                </>
              : <>
                  Cherchez une option{" "}
                  <span className="font-semibold">Installer</span> ou{" "}
                  <span className="font-semibold">
                    Ajouter à l'écran d'accueil
                  </span>{" "}
                  dans le menu de votre navigateur.
                </>
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="secondary">Fermer</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
